import { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Sparkles, Check, Download } from 'lucide-react';
import {
  Eyebrow, Tom, Crowd, MarginNote, MarkerHighlight, EvidenceCard,
} from '@/components/foundry';
import { computeBlueprintFromSubmissions, V2_REQUIRED_LESSON_SLUGS } from '@/services/kickstartBlueprint';
import { useAppAuth } from '@/lib/appAuth';

interface Props {
  onBack: () => void;
  /**
   * Open the personalized Bridge once a blueprint has been computed (or recovered as an
   * existing draft). Wires through KickstartV2App.navigate so the address bar updates
   * to /bridge/<id>.
   */
  onOpenBridge: (blueprintId: string) => void;
}

export default function BreakthroughPage({ onBack, onOpenBridge }: Props) {
  const { getToken } = useAppAuth();
  const [computeState, setComputeState] = useState<'idle' | 'computing' | 'error'>('idle');
  const [computeError, setComputeError] = useState<string | null>(null);
  const [missingLessonHint, setMissingLessonHint] = useState<string | null>(null);

  async function handleViewBridge() {
    if (computeState === 'computing') return;
    setComputeState('computing');
    setComputeError(null);
    setMissingLessonHint(null);
    try {
      const token = (await getToken?.({ skipCache: true })) ?? null;
      const result = await computeBlueprintFromSubmissions(
        Array.from(V2_REQUIRED_LESSON_SLUGS),
        token,
      );
      if (!result?.id) {
        throw new Error('Blueprint compute returned no id');
      }
      window.scrollTo(0, 0);
      onOpenBridge(result.id);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not compute your blueprint.';
      // computeFromSubmissions throws "missing lab work for lessons: 03, 05" style errors
      // — surface that as a Builder-readable hint instead of a raw stack.
      const lessonsMatch = message.match(/lessons?:\s*([0-9 ,]+)/i);
      if (lessonsMatch) {
        const lessons = lessonsMatch[1]
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
        setMissingLessonHint(
          lessons.length === 1
            ? `Finish lesson ${lessons[0]} first — its Lab Work is what we use to draft the bridge.`
            : `Finish lessons ${lessons.join(', ')} first — their Lab Work is what we use to draft the bridge.`,
        );
      } else {
        setComputeError(message);
      }
      setComputeState('error');
    }
  }

  return (
    <div className="mx-auto max-w-[1280px] px-4 py-7 sm:px-6 lg:px-8">
      {/* Back */}
      <button
        onClick={onBack}
        className="mb-6 inline-flex items-center gap-1.5 font-geist text-[13px] font-medium text-fd-text-muted transition-colors hover:text-fd-text"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Back to Kickstart
      </button>

      {/* HERO */}
      <section className="relative mb-10 overflow-hidden rounded-[14px] border-[1.5px] border-fd-text bg-fd-accent p-8 sm:p-10" style={{ boxShadow: '6px 6px 0 0 var(--fd-text)' }}>
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-[1.3fr_1fr]">
          <div>
            <div className="mb-3 inline-flex items-center gap-2 rounded-[4px] border-[1.5px] border-fd-text bg-fd-text px-3 py-1.5 font-geist-mono text-[11px] font-bold uppercase tracking-[0.10em] text-fd-accent">
              <Sparkles className="h-3.5 w-3.5" />
              The Breakthrough · Unlocked
            </div>
            <h1 className="font-geist text-[36px] font-semibold leading-[1.02] tracking-[-0.035em] text-fd-text [text-wrap:balance] sm:text-[44px] lg:text-[52px]">
              You did the work.
              <br />
              Here's the{' '}
              <MarkerHighlight tone="ink"><span className="text-fd-bg">first move</span></MarkerHighlight>.
            </h1>
            <p className="mt-5 max-w-[540px] text-[15px] leading-[1.55] text-fd-text/80 sm:text-[16px] lg:text-[17px]">
              Five lessons, five Lab Work submissions. You named the Pocket Genius problem in your
              own business, made your Steering Layer explicit, and drafted the first warm bridge
              for your real offer. That's more than most paid courses ship.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <button
                onClick={handleViewBridge}
                disabled={computeState === 'computing'}
                className="inline-flex h-12 items-center gap-2 rounded-[4px] border-[1.5px] border-fd-text bg-fd-text px-5 font-geist-mono text-[14px] font-bold uppercase tracking-[0.04em] text-fd-accent transition-all hover:-translate-x-px hover:-translate-y-px disabled:cursor-not-allowed disabled:opacity-70"
                style={{ boxShadow: '3px 3px 0 0 var(--fd-paper)' }}
              >
                {computeState === 'computing' ? 'Drafting your bridge…' : 'View your bridge'}
                <ArrowRight className="h-3.5 w-3.5" />
              </button>
              <button className="inline-flex h-12 items-center gap-2 rounded-[4px] border-[1.5px] border-fd-text bg-fd-paper px-5 font-geist text-[14px] font-semibold text-fd-text transition-colors hover:bg-fd-bg">
                Read what's inside
              </button>
            </div>
            {missingLessonHint && (
              <p
                role="status"
                className="mt-4 max-w-[540px] rounded-[6px] border-[1.5px] border-fd-text bg-fd-paper px-3 py-2 font-geist text-[13px] leading-[1.5] text-fd-text"
              >
                {missingLessonHint}
              </p>
            )}
            {computeState === 'error' && computeError && (
              <p
                role="alert"
                className="mt-4 max-w-[540px] rounded-[6px] border border-fd-coral/40 bg-fd-coral-soft px-3 py-2 font-geist text-[13px] leading-[1.5] text-fd-text"
              >
                Couldn't draft your bridge: {computeError}. Try once more — your Lab Work is saved.
              </p>
            )}
          </div>

          {/* Crowd cheering on the right */}
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="relative hidden lg:flex lg:items-end lg:justify-center"
          >
            <Crowd variant="cheering" className="h-[220px] w-auto" />
            <MarginNote tilt={-4} tone="ink" size="md" className="absolute right-2 top-2 bg-fd-paper px-2 py-0.5 rounded-[3px] border border-fd-text">
              cohort 04 just clapped.
            </MarginNote>
          </motion.div>
        </div>
      </section>

      {/* THE GIFT — what they actually unlocked */}
      <section className="mb-10 grid grid-cols-12 gap-5">
        {/* Bridge draft asset (col-8) */}
        <article className="col-span-12 rounded-[14px] border-[1.5px] border-fd-text bg-fd-paper p-7 lg:col-span-8" style={{ boxShadow: '5px 5px 0 0 var(--fd-text)' }}>
          <div className="mb-4 flex flex-wrap items-baseline justify-between gap-3">
            <div>
              <Eyebrow className="mb-1.5">YOUR ASSET · WARM BRIDGE V1</Eyebrow>
              <h2 className="font-geist text-[22px] font-medium tracking-[-0.015em]">
                A first-draft warm bridge written from your Lab Work.
              </h2>
            </div>
            <button className="inline-flex h-9 items-center gap-1.5 rounded-[4px] border border-fd-border bg-fd-bg px-3 font-geist text-[12.5px] font-medium text-fd-text-muted transition-colors hover:bg-fd-surface-1 hover:text-fd-text">
              <Download className="h-3.5 w-3.5" />
              Download as .md
            </button>
          </div>

          <div className="rounded-[10px] border border-fd-border bg-fd-bg p-5 font-geist text-[14px] leading-[1.65] text-fd-text">
            <p className="mb-3">
              <span className="font-geist-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-fd-text-subtle">Subject:</span>{' '}
              quick read for {`{first_name}`} — re: the AI thing
            </p>
            <hr className="my-3 border-fd-border" />
            <p className="mb-3">Hey {`{first_name}`},</p>
            <p className="mb-3">
              Saw you signed up for the joint pain toolkit. Quick honest note before we send the rest of
              the welcome stuff.
            </p>
            <p className="mb-3">
              The reason we built this isn't because clinics need more content — your members already
              have ChatGPT in their pocket. The 47-lesson library era is over. What still works is your
              <i> method</i>: the order you ask for evidence, the way you frame the first 30 days, the
              standard you hold for a "ready to ship" plan.
            </p>
            <p className="mb-3">
              That's what the toolkit teaches. And if it lands, the next step is the full Lab — but
              you'll know that for yourself by lesson 3.
            </p>
            <p>— Tom</p>
          </div>

          <p className="mt-4 text-[12.5px] leading-[1.55] text-fd-text-muted">
            This is yours. Drop it into your email tool, swap the curly placeholders for real names,
            and ship it. Two cohort 03 members sent a version of this to a cold list the same day
            and got first replies inside 24 hours.
          </p>
        </article>

        {/* Lab signoff (col-4) */}
        <article className="col-span-12 rounded-[14px] border-[1.5px] border-fd-text bg-fd-paper p-6 lg:col-span-4" style={{ boxShadow: '5px 5px 0 0 var(--fd-text)' }}>
          <div className="mb-3"><Eyebrow>TOM'S SIGNOFF</Eyebrow></div>
          <EvidenceCard kind="verdict" eyebrow="VERDICT · KICKSTART COMPLETE">
            Your Lab Work shows you understood the shift, named your method, and produced a draft.
            That's the bar. <b>The Lab is the right next step</b> — your Game Plan will start pre-loaded
            with everything from here.
          </EvidenceCard>

          <div className="mt-4 grid grid-cols-3 gap-2 text-center">
            <Stat label="Lessons" value="5/5" />
            <Stat label="Lab Work" value="5/5" />
            <Stat label="Cohort" value="04" />
          </div>
        </article>
      </section>

      {/* WHAT'S NEXT — Bridge CTA band (the pricing/checkout call lives on the Bridge,
          where the verdict is personalized; here we just route the Builder forward). */}
      <section className="relative mb-10 overflow-hidden rounded-[14px] border-[1.5px] border-fd-text bg-fd-text p-8 text-fd-bg sm:p-10" style={{ boxShadow: '5px 5px 0 0 var(--fd-accent)' }}>
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-[auto_1fr_auto]">
          <Tom variant="pointing-right" className="hidden h-[180px] w-auto lg:block" />
          <div>
            <Eyebrow className="mb-2 block !text-fd-accent">NEXT · YOUR PERSONALIZED BRIDGE</Eyebrow>
            <h2 className="font-geist text-[26px] font-medium tracking-[-0.02em]">
              You drafted it. Now see the{' '}
              <span className="inline-block rounded-[4px] bg-fd-accent px-2 text-fd-text" style={{ boxShadow: '3px 3px 0 0 var(--fd-paper)' }}>
                exact engine
              </span>
              {' '}we'd build for you.
            </h2>
            <p className="mt-3 max-w-[560px] text-[14px] leading-[1.55] text-fd-bg/80">
              Your bridge is a one-page verdict drafted from your Lab Work — the wrapper that fits
              your business, the first scope, what we're <i>not</i> doing first, and the one question
              your buyer is asking. Open it, then decide whether to have us build it.
            </p>
            <div className="mt-5 flex flex-wrap gap-2.5">
              <FeatureChip>Recommended wrapper</FeatureChip>
              <FeatureChip>First build scope</FeatureChip>
              <FeatureChip>Hero promise · proof · objection</FeatureChip>
              <FeatureChip>Your next move</FeatureChip>
            </div>
          </div>
          <div className="grid gap-2.5">
            <button
              onClick={handleViewBridge}
              disabled={computeState === 'computing'}
              className="inline-flex h-12 items-center gap-2 rounded-[4px] border-[1.5px] border-fd-text bg-fd-accent px-5 font-geist-mono text-[14px] font-bold uppercase tracking-[0.04em] text-fd-text transition-all hover:-translate-x-px hover:-translate-y-px disabled:cursor-not-allowed disabled:opacity-70"
              style={{ boxShadow: '3px 3px 0 0 var(--fd-paper)' }}
            >
              Apply now · $1,997
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
            <p className="font-geist-mono text-[10.5px] font-semibold uppercase tracking-[0.06em] text-fd-bg/60">
              {computeState === 'computing'
                ? 'Drafting from your Lab Work…'
                : 'Built from all 5 Lab Work submissions'}
            </p>
          </div>
        </div>
      </section>

      {/* QUICK WINS — 3 things to do next */}
      <section className="rounded-[14px] border border-dashed border-fd-border-strong/40 bg-fd-paper p-7">
        <Eyebrow className="mb-2 block">YOUR FIRST 3 MOVES · THIS WEEK</Eyebrow>
        <h3 className="mb-5 font-geist text-[20px] font-medium tracking-[-0.015em]">
          Whatever you decide on the bridge — do these too.
        </h3>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <Move num="1" title="Send the bridge to 10 cold contacts" body="Use the draft above. Swap names. Don't polish it. Ship by Friday." />
          <Move num="2" title="Reply to 3 of them by hand" body="Whatever they say, reply in YOUR voice — proof to yourself the bridge works without you." />
          <Move num="3" title="Re-watch lesson 03 in 7 days" body="What you missed first time will hit different once you've actually run the bridge." />
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-[8px] border border-fd-border bg-fd-bg p-2.5">
      <div className="font-geist-mono text-[18px] font-semibold leading-none tracking-[-0.02em] text-fd-text">{value}</div>
      <div className="mt-1 font-geist-mono text-[9.5px] font-semibold uppercase tracking-[0.06em] text-fd-text-subtle">{label}</div>
    </div>
  );
}

function FeatureChip({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-[4px] border border-fd-bg/30 bg-fd-text/30 px-2.5 py-1 font-geist text-[12px] font-medium text-fd-bg">
      <Check className="h-3 w-3 text-fd-accent" />
      {children}
    </span>
  );
}

function Move({ num, title, body }: { num: string; title: string; body: string }) {
  return (
    <div className="rounded-[10px] border border-fd-border bg-fd-bg p-4">
      <div className="mb-2 inline-flex items-center gap-2">
        <span className="grid h-6 w-6 place-items-center rounded-[4px] bg-fd-accent font-geist-mono text-[12px] font-bold text-fd-text">
          {num}
        </span>
        <h4 className="font-geist text-[14px] font-semibold tracking-[-0.005em]">{title}</h4>
      </div>
      <p className="text-[12.5px] leading-[1.5] text-fd-text-muted">{body}</p>
    </div>
  );
}
